// Environment Configuration
// Supports Vite build-time env variables

// Helper function to get environment variable with fallback priority:
// 1. Vite build-time env (import.meta.env)
// 2. Default values
// 3. Returns empty string if not found
function getEnvVar(viteKey: keyof ImportMetaEnv, defaultValue?: string): string {
    // Vite build-time variable
    // In production, these are injected by the build process
    // Note: We use indexed access to prevent some bundlers from aggressively replacing typical usage
    const buildValue = import.meta.env[viteKey];
    if (buildValue && buildValue.trim() !== '') {
        return buildValue;
    }

    // Default value
    if (defaultValue !== undefined) {
        return defaultValue;
    }

    return '';
}

// Environment variable exports
export const API_BASE = getEnvVar('VITE_API_BASE');
export const OAUTH2_CLIENT_ID = getEnvVar('VITE_OAUTH2_CLIENT_ID');
export const OAUTH2_REDIRECT_URI = getEnvVar(
    'VITE_OAUTH2_REDIRECT_URI',
    typeof window !== 'undefined' ? `${window.location.origin}/oauth-callback` : ''
);
export const OAUTH2_SCOPES = getEnvVar(
    'VITE_OAUTH2_SCOPES',
    'openid profile email'
);

// OAuth2 Logout Configuration
export const OAUTH2_LOGOUT_REDIRECT_URI = getEnvVar(
    'VITE_OAUTH2_LOGOUT_REDIRECT_URI',
    typeof window !== 'undefined' ? `${window.location.origin}/login` : ''
);

// Construct OAuth2 logout endpoint URL
export const OAUTH2_LOGOUT_URI = `${API_BASE}/oauth2/logout`;

// Validation
const missingVars: string[] = [];

if (!API_BASE || API_BASE.trim() === '') {
    missingVars.push('VITE_API_BASE');
}

if (!OAUTH2_CLIENT_ID || OAUTH2_CLIENT_ID.trim() === '') {
    missingVars.push('VITE_OAUTH2_CLIENT_ID');
}

if (missingVars.length > 0) {
    const errorMessage = `
╔════════════════════════════════════════════════════════════════╗
║  Missing Required Environment Variables                        ║
╠════════════════════════════════════════════════════════════════╣
║  Missing: ${missingVars.join(', ').padEnd(48)} ║
╠════════════════════════════════════════════════════════════════╣
║  Local Development:                                            ║
║    Set in .env.local file                                      ║
║                                                                ║
║  Production:                                                   ║
║    Set in Pulumi config (setup.properties)                     ║
║    Run: npm run pulumi:setup or npm run pulumi:deploy          ║
╚════════════════════════════════════════════════════════════════╝
    `.trim();
    
    console.error(errorMessage);
    
    // Throw error to prevent app from starting with invalid config
    throw new Error(`Missing required environment variables: ${missingVars.join(', ')}`);
}

// Legacy ENV object for backward compatibility
export const ENV = {
    API_BASE,
    OAUTH2_CLIENT_ID,
    OAUTH2_REDIRECT_URI,
    OAUTH2_SCOPES,
};
